import { useState } from 'react';

import { Settings as SettingsIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';

import type { Credential } from '@/types';

import { ManageEnvironmentVariables } from './ManageEnvironmentVariables';

interface EnvironmentVariablesProps {
  tenantId: string;
  envName: string;
  envId: string;
  credentials: Credential[];
}

interface EnvVariable {
  id: number;
  name: string;
  value: string | number;
  sensitive?: boolean;
  datatype?: string;
}

/**
 * Environment Variables section matching production dataProtect
 * - Simple table view (no inline edit/delete buttons)
 * - "Manage" button opens modal for all edits
 */
export const EnvironmentVariables = ({
  tenantId,
  envName,
  envId,
  credentials,
}: EnvironmentVariablesProps) => {
  const [manageDialogOpen, setManageDialogOpen] = useState(false);

  // Find environment variables credential
  const envVarsCred = credentials.find((c) => c.credentialType === 'EnvironmentVariables');

  // Environment variables are stored in credentialsJson.envVars
  const variables: EnvVariable[] = (envVarsCred?.credentialsJson?.envVars as EnvVariable[]) || [];

  // Sort alphabetically by variable name
  const sortedVariables = [...variables].sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  );

  const maskValue = (value: string | number): string => {
    const str = String(value ?? '');
    return '*'.repeat(Math.min(str.length, 20)) || '********';
  };

  return (
    <Box sx={{ mt: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.6,
        }}
      >
        <Typography sx={{ fontWeight: 'bold', fontSize: '13px', px: 0.75 }}>
          {variables.length > 0 ? 'Environment Variables' : 'No Environment Variables'}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<SettingsIcon sx={{ height: 24, width: 24 }} />}
          onClick={() => setManageDialogOpen(true)}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            height: 32,
            width: 115,
            fontWeight: 'bold',
            borderColor: '#ccc',
            color: '#333',
            marginTop: '-8px',
            '&:hover': {
              borderColor: '#999',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Manage
        </Button>
      </Box>

      {variables.length > 0 && (
        <Box sx={{ backgroundColor: '#EEE', width: '100%' }}>
          <TableContainer>
            <Table sx={{ border: '1px solid #ddd' }}>
              <TableBody>
                {sortedVariables.map((v) => (
                  <TableRow key={v.id || v.name}>
                    <TableCell sx={{ width: '40vw' }}>
                      <Typography>{v.name}</Typography>
                    </TableCell>
                    <TableCell sx={{ width: '40vw' }}>
                      <Typography className="text">
                        {v.sensitive ? maskValue(v.value) : String(v.value ?? '')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Manage Variables Modal */}
      <Dialog
        open={manageDialogOpen}
        onClose={(_event, reason) => {
          // Only close on explicit Cancel button click, not on backdrop click or escape
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          setManageDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        slotProps={{
          paper: {
            sx: {
              maxWidth: '90vw',
            },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Environment Variables (User Generated)
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#eee' }}>
          <ManageEnvironmentVariables
            tenantId={tenantId}
            envName={envName}
            envId={envId}
            envVarsCred={envVarsCred}
            onClose={() => setManageDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};
